import { FC } from "react";
import {
  getDataAutomationGroup,
  getDataAutomationId,
  getDataLinkAttributes,
} from "@/helpers";
import Link from "@/components/Link";
import { CaretRightLine } from "@/icons";
import { GlobalFooterProps } from "./GlobalFooter";

export const GlobalFooterRedesigned: FC<
  Omit<GlobalFooterProps, "isRedesigned">
> = ({ logo, description, socials, links, theme, cta, socialsText }) => {
  return (
    <footer
      data-theme={theme}
      className="bg-background mt-auto overflow-x-hidden"
      {...getDataAutomationId("footer")}
    >
      <div className="footer-gradient-line" />
      <div className="swdc-container flex flex-col items-start gap-16 py-16 sm:items-center sm:gap-20 sm:py-20 lg:items-start lg:gap-25 lg:py-25">
        <div className="flex flex-col items-start justify-between gap-12 sm:items-center sm:gap-14 lg:flex-row lg:items-start">
          <div className="flex max-w-full flex-col gap-6 sm:max-w-104 md:max-w-130.5 lg:w-full lg:max-w-126">
            <div className="max-h-7.5 max-w-37.5">{logo}</div>
            {description}
          </div>
          <div className="flex w-full flex-col items-center gap-5">
            {cta}
            <div
              className="flex flex-col gap-2.5 sm:flex-row sm:items-center"
              {...getDataAutomationGroup("social-icons")}
            >
              <div className="w-full">{socialsText && socialsText}</div>
              <ul className="flex items-center gap-3 lg:w-full">
                {socials &&
                  socials.length > 0 &&
                  socials.map(({ link, icon }, index) => (
                    <li
                      key={`${link.title}-${index}`}
                      className="z-10 flex size-10 max-w-10 cursor-pointer items-center justify-center"
                      {...getDataAutomationId("social-media-icon")}
                    >
                      <Link
                        href={link.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        {...getDataLinkAttributes({
                          dlt: link.dlt || "Footer",
                          dld: link.dld || `${link.title} social icon`,
                        })}
                        aria-label={link.title}
                        className="bg-background border-swdc-gray-100 hover:border-swdc-orange-400 active:border-swdc-orange-200 active:bg-swdc-orange-200 focus-visible:outline-swdc-blue-325 hover:bg-swdc-orange-400 rounded-full border -outline-offset-3 outline-none focus-visible:outline-2 focus-visible:outline-solid"
                        {...getDataAutomationGroup("link")}
                      >
                        {icon}
                      </Link>
                    </li>
                  ))}
              </ul>
            </div>
          </div>
        </div>
        <div
          className="lg:divide-swdc-warm-gray-100 grid w-full max-w-full grid-cols-1 gap-x-8 gap-y-8 sm:max-w-104 sm:grid-cols-2 sm:gap-y-10 md:max-w-130.5 lg:max-w-full lg:grid-cols-5 lg:divide-x"
          {...getDataAutomationGroup("navigation-links")}
        >
          {links.map(({ link_category, link }, index) => (
            <div
              key={`${link_category ?? "category"}-${index}`}
              className="not-first:border-swdc-warm-gray-100 not-first:before:bg-swdc-orange-400 relative flex break-inside-avoid flex-col not-first:border-t not-first:pt-8 not-first:content-[''] not-first:before:absolute not-first:before:-top-1 not-first:before:size-2 sm:max-lg:nth-2:border-t-0 sm:max-lg:nth-2:pt-0 sm:max-lg:nth-2:before:hidden lg:pr-8 lg:not-first:border-t-0 lg:not-first:pt-0 lg:not-first:before:absolute lg:not-first:before:top-0 lg:not-first:before:-left-9 lg:not-first:before:size-2 lg:not-first:before:content-[''] lg:first:pl-0 lg:last:pr-0"
            >
              {link_category && (
                <h2 className="text-swdc-gray-300 text-body-2xs mb-5 font-mono tracking-[0.1875rem] uppercase">
                  {link_category}
                </h2>
              )}
              <ul>
                {link.map(({ title, href, dlt, dld }, linkIndex) => (
                  <li
                    key={`${index}-${linkIndex}-${title}`}
                    className="mb-2 last:mb-0"
                    {...getDataAutomationId("navigation-link")}
                  >
                    <Link
                      className="group font-geist text-link-xs text-swdc-gray-250 hover:text-swdc-gray-350 active:text-swdc-gray-200 focus-visible:outline-swdc-blue-325 h-fit rounded-xs leading-6 outline-none focus-visible:outline-2 focus-visible:outline-solid"
                      href={href}
                      {...getDataLinkAttributes({
                        dlt: dlt || "Footer",
                        dld: dld || `${title} link`,
                      })}
                    >
                      <span className="inline-flex items-center gap-1.5">
                        <span>{title}</span>
                        <span className="text-swdc-orange-400 group-active:text-swdc-orange-200 relative inline-flex h-4 w-4 shrink-0 items-center overflow-hidden after:absolute after:top-1/2 after:left-0 after:h-px after:w-2.5 after:origin-left after:-translate-y-1/2 after:scale-x-0 after:bg-current after:transition-transform after:duration-260 after:ease-linear group-hover:after:scale-x-100">
                          <CaretRightLine
                            width={14}
                            height={14}
                            fill="currentColor"
                            className="absolute top-1/2 left-0 z-10 -translate-y-1/2 opacity-0 transition-transform duration-260 ease-linear group-hover:translate-x-0.5 group-hover:opacity-100"
                          />
                        </span>
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </footer>
  );
};

export default GlobalFooterRedesigned;
